import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GraphHeader from "../../CommonComponents/GraphHeader";
import FecthingPage from "../../CommonComponents/FecthingPage";
import { ProductivityTrackerThunk } from "../../../../../redux/features/Settings/Statistics";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";

const ProductivityTracker = () => {
  const defaultGraphData = {
    createdTransactions: 20,
    approvedTransactions: 20,
  };
  const [graphData, setGraphData] = useState(defaultGraphData);

  const [displayData, setDisplayData] = useState({
    tableRows: Array.from({ length: 4 }).map((_, idx) => ({
      id: idx,
      name: "-",
      userId: "-",
      created: "-",
      approved: "-",
    })),
    totalCreated: "-",
    totalApproved: "-",
  });

  const [selectedDays, setSelectedDays] = useState("last 7 days");
  const [selectedTxnOption, setSelectedTxnOption] = useState("bene");
  const [selectedAmount, setSelectedAmount] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [fetchPageEnabled, setFetchPageEnabled] = useState(true);
  const [isError, setIsError] = useState(false);
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);
  const { ProductivityTrackerThunkLoading, ProductivityTrackerThunkError } =
    useSelector((state) => state.settingStatistics);

  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        setRawReportData(reportData);

        const rows = reportData.productivity_tracker?.length
          ? reportData.productivity_tracker.map((emp, idx) => ({
              id: idx,
              name: emp.user_full_name || "-",
              userId: emp.user_id || "-",
              created: emp.transaction_created_count ?? "-",
              approved: emp.transaction_approved_count ?? "-",
            }))
          : Array.from({ length: 4 }).map((_, idx) => ({
              id: idx,
              name: "-",
              userId: "-",
              created: "-",
              approved: "-",
            }));

        setDisplayData({
          tableRows: rows,
          totalCreated: reportData.total_transaction_created_count ?? "-",
          totalApproved: reportData.total_transaction_approved_count ?? "-",
        });

        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(defaultGraphData);
        setFetchPageEnabled(response?.payload?.status !== 404);
        toastShownRef.current = false;
        setDisplayData({
          tableRows: Array.from({ length: 4 }).map((_, idx) => ({
            id: idx,
            name: "-",
            userId: "-",
            created: "-",
            approved: "-",
          })),
          totalCreated: "-",
          totalApproved: "-",
        });
        setIsError(true);
      }
    },
    [dispatch, entityId, selectedDays, selectedTxnOption, selectedEmployee],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!entityId) return;

    const response = await dispatch(
      ProductivityTrackerThunk({
        user_ids: selectedEmployee,
        period: selectedDays,
        entity_id: entityId,
        reportType: selectedTxnOption === "bene" ? "beneficiary" : "internal",
      }),
    );

    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  useEffect(() => {
    if (!fetchPageEnabled) {
      fetchData();
    }
  }, [fetchPageEnabled, selectedDays, selectedEmployee, selectedTxnOption]);

  return (
    <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md">
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "txnOption", setterState: setSelectedTxnOption },
          { type: "employees", setterState: setSelectedEmployee },
        ]}
        titleText={"Productivity tracker"}
      />
      <div className="flex flex-col relative">
        <FecthingPage
          fetchPageEnabled={fetchPageEnabled}
          loading={ProductivityTrackerThunkLoading || fetchPageLoading}
          isError={isError}
          errorData={ProductivityTrackerThunkError}
          handleFunction={handleFetchClick}
        />
        <div className="w-width-full">
          <div className="h-[var(--statistic-graph-height)] w-width-full bg-primary-card overflow-y-auto">
            <div className="flex font-sans text-font-xs font-normal text-text-secondary bg-primary-nav-act">
              <div className="flex-1 py-padding-sm">
                <span className="Qmd:pl-padding-lg pl-padding-md Qsm:pr-padding-sm flex Qsm:justify-start justify-center items-center">
                  Employee’s name and user ID
                </span>
              </div>
              <div className="flex-1 text-center py-padding-sm border-r border-l border-primary-card">
                Transactions created
              </div>
              <div className="flex-1 text-center py-padding-sm">
                Transactions approved
              </div>
            </div>
            {displayData.tableRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-center font-sans font-normal text-text-secondary bg-primary-card py-padding-sm"
              >
                <div className="flex-1">
                  <div className="text-font-xs Qmd:pl-padding-lg pl-padding-md capitalize">
                    {row.name}
                  </div>
                  <div className="text-font-xxs Qmd:pl-padding-lg pl-padding-md">
                    {row.userId}
                  </div>
                </div>
                <div className="flex-1 text-font-xs text-center py-padding-sm border-r border-l border-primary-bg">
                  {row.created}
                </div>
                <div className="flex-1 text-font-xs text-center py-padding-sm">
                  {row.approved}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qmd:items-center Qmd:justify-center py-padding-sm flex-1 Qmd:flex-row flex-col Qmd:gap-gap-base gap-gap-sm">
            <div className="flex-1 flex-shrink-0 Qmd:flex hidden"></div>
            <div className="flex-1 flex-shrink-0 flex text-font-xs text-center Qmd:border-r border-primary-bg justify-around gap-gap-lg">
              <div className="flex flex-1 Qmd:justify-end Qmd:pr-padding-lg Qmd:mx-margin-base mx-margin-md gap-gap-md Qmd:border-none border-b border-primary-bg Qmd:pb-padding-base pb-padding-sm">
                <div className="text-text-secondary text-font-xxs">
                  Total transactions created :
                </div>
                <div className="text-font-xxs font-semibold">
                  {displayData.totalCreated}
                </div>
              </div>
            </div>
            <div className="flex-1 flex-shrink-0 flex text-font-xs text-center justify-around gap-gap-lg">
              <div className="flex flex-1 Qmd:pl-padding-lg pl-padding-md gap-gap-md">
                <div className="text-text-secondary text-font-xxs">
                  Total transactions approved :
                </div>
                <div className="text-font-xxs font-semibold">
                  {displayData.totalApproved}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductivityTracker;
